import { useCallback, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import type { BlockOf, DesignId, Site } from '../model/types';
import { findBlock, firstVisibleOfType, visibleEntries } from '../model/catalog';
import { fmtDate } from '../model/format';
import { APP_URL } from '../config';
import { Hero, Ornament } from './Hero';
import { BlockView } from './Blocks';
import { RsvpDialog, type SubmitRsvp } from './RsvpDialog';
import { EditChip, type EditTarget } from './parts';
import { siteClass, siteStyle, type SiteMode } from './theme';

interface SiteViewProps {
  site: Site;
  mode: Exclude<SiteMode, 'thumb'>;
  onEdit?: (t: EditTarget) => void;
  onRsvp?: (blockId: string) => void;
  onNav?: (entryId: string) => void;
}

/** A whole wedding website. */
export function SiteView({ site: s, mode, onEdit, onRsvp, onNav }: SiteViewProps) {
  const vis = visibleEntries(s);
  const navItems = vis.filter((e) => e.kind === 'group' || (e.type !== 'rsvp' && !(e.type === 'gallery' && !e.showTitle)));
  const rsvp = firstVisibleOfType(s, 'rsvp');
  const p = mode[0];
  const f = s.footer, h = s.header;
  const fdate = fmtDate(h.date, s.design.dateFormat);
  return (
    <div className={siteClass(s, undefined, mode)} style={siteStyle(s)}>
      {(navItems.length > 0 || rsvp) && (
        <nav className="s-nav" aria-label="Sections">
          {navItems.map((e) => (
            <a key={e.id} href={`#sec-${p}${e.id}`} onClick={(ev) => { if (onNav) { ev.preventDefault(); onNav(e.id); } }}>{e.title}</a>
          ))}
          {rsvp && <button type="button" className="s-btn" onClick={() => onRsvp?.(rsvp.id)}>RSVP</button>}
        </nav>
      )}
      <Hero site={s} mode={mode} onEdit={onEdit} onRsvp={onRsvp} />
      <main>
        {vis.map((e, i) => {
          const blocks = e.kind === 'group' ? e.items.filter((b) => !b.hidden) : [e];
          const onlyGal = blocks.every((b) => b.type === 'gallery' && !b.showTitle);
          return (
            <section key={e.id} className={`s-sec${onlyGal ? ' only-gal' : ''}`} id={`sec-${p}${e.id}`} data-sec={e.id}>
              {i > 0 && !onlyGal && <Ornament site={s} />}
              {blocks.map((b) => <BlockView key={b.id} block={b} site={s} mode={mode} onEdit={onEdit} onRsvp={onRsvp} />)}
            </section>
          );
        })}
      </main>
      <footer className="s-foot" {...(mode === 'edit' ? { 'data-edit': '' } : {})}>
        <EditChip mode={mode} label="Edit footer" onEdit={onEdit} target={{ kind: 'footer' }} />
        <h2>{h.name1} <i>&amp;</i> {h.name2}</h2>
        {(fdate || h.venue) && <p className="sf-date">{[fdate, h.venue].filter(Boolean).join(', ')}</p>}
        {f.message && <p className="sf-msg">{f.message}</p>}
        {f.hashtag && <p className="sf-tag">{f.hashtag}</p>}
        {f.showMade && (
          <p className="sf-made">
            {mode === 'live'
              ? <a href={APP_URL + '/'} style={{ textDecoration: 'none' }}>Made with <img src="/kovio-mark.png" alt="" width={12} height={13} /><b>kovio</b></a>
              : <>Made with <img src="/kovio-mark.png" alt="" width={12} height={13} /><b>kovio</b></>}
          </p>
        )}
      </footer>
    </div>
  );
}

export interface SiteFrameHandle {
  /** Scrolls the website so the element is in view, below the menu bar. */
  scrollTo: (selector: string, flash?: boolean) => void;
  closeRsvp: () => void;
  element: () => HTMLDivElement | null;
}

interface SiteFrameProps {
  site: Site;
  mode: Exclude<SiteMode, 'thumb'>;
  className: string;
  id?: string;
  label?: string;
  onEdit?: (t: EditTarget) => void;
  /** Saves RSVP replies. Leave out for previews. */
  submitRsvp?: SubmitRsvp;
  children?: ReactNode;
  ref?: Ref<SiteFrameHandle>;
}

/** A scrolling box that shows a website, with the RSVP pop-up and in-page navigation. */
export function SiteFrame({ site, mode, className, id, label, onEdit, submitRsvp, children, ref }: SiteFrameProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [rsvpId, setRsvpId] = useState<string | null>(null);

  const scrollTo = useCallback((selector: string, flash = false) => {
    const box = boxRef.current;
    const t = box && box.querySelector<HTMLElement>(selector);
    if (!box || !t) return;
    const nav = box.querySelector<HTMLElement>('.s-nav');
    const top = t.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - (nav ? nav.offsetHeight : 0) - 16;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    if (flash) { t.classList.remove('s-flash'); void t.offsetWidth; t.classList.add('s-flash'); }
  }, []);

  useImperativeHandle(ref, () => ({ scrollTo, closeRsvp: () => setRsvpId(null), element: () => boxRef.current }), [scrollTo]);

  const hit = rsvpId ? findBlock(site, rsvpId) : null;
  const block = hit && hit.block.type === 'rsvp' ? (hit.block as BlockOf<'rsvp'>) : null;
  const closeRsvp = useCallback(() => setRsvpId(null), []);

  return (
    <div className={className} id={id} ref={boxRef} aria-label={label}>
      {children}
      <SiteView site={site} mode={mode} onEdit={onEdit} onRsvp={setRsvpId} onNav={(eid) => scrollTo(`[data-sec="${CSS.escape(eid)}"]`)} />
      {block && boxRef.current && <RsvpDialog site={site} block={block} container={boxRef.current} submit={submitRsvp} onClose={closeRsvp} />}
    </div>
  );
}

/** A small, scaled-down picture of a website's header. */
export function Thumb({ site, designId, label }: { site: Site; designId?: DesignId; label?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => setScale(el.clientWidth / 1200);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="thumb" ref={ref} {...(label ? { role: 'img', 'aria-label': label } : {})}>
      <div className={`thumb-inner ${siteClass(site, designId, 'thumb')}`} style={{ ...siteStyle(site, designId), transform: `scale(${scale})` }}>
        <Hero site={site} designId={designId} mode="thumb" />
      </div>
    </div>
  );
}
